import multiClassToggle from '../utils/multiClassToggle';

// Scrollyfill is a polyfill for the 'scrollend' event, which is currently
// only supported in firefox, but not in chrome, chromium based browsers or
// safari.
import 'scrollyfills';

interface SliderOptions {
	alignment?: 'start' | 'middle' | 'end';
	autoPlayDelay?: number;
	autoPlayOnInit?: boolean;
	maxVisibleSlides?: number;
}

export class UfrSlider {

	// Static members
	#sliderWrapper: HTMLDivElement;

	// Variable members
	#slidingElement: HTMLUListElement;
	#dotNavigationWrapper: HTMLUListElement;
	#playPauseButton: HTMLButtonElement;
	#dotNavigationElements: NodeListOf<HTMLLIElement>;
	#originalSliderElements: NodeListOf<HTMLLIElement>;
	#totalSliderElements: NodeListOf<HTMLLIElement>;
	#chevronLeftElement: HTMLButtonElement;
	#chevronRightElement: HTMLButtonElement;

	// State members
	#isPlaying: boolean;
	#activeSlideElement: HTMLLIElement;
	#activeSlideIndex: number;
	#isAdjustingScroll: boolean;
	#autoPlayInterval: NodeJS.Timer;

	#options: SliderOptions;

	constructor (
		sliderWrapper: HTMLDivElement,
		options?: SliderOptions,
	) {
		this.#options = {
			alignment: 'middle',
			autoPlayDelay: 5000,
			autoPlayOnInit: false,
			maxVisibleSlides: 5,
			...options,
		};
		this.#sliderWrapper = sliderWrapper;
		this.#isPlaying = this.#options.autoPlayOnInit;
		this.#isAdjustingScroll = false;

		this.#initSubElements();

		this.#initEventHandlers();

		this.#duplicateSlides();

		this.#initNavigationPills();

		this.#eventHandlers.handleActiveSlide();
		this.#eventHandlers.resetScrollPosition();
		requestAnimationFrame( () => {
			this.goToSlide( 0, false, 'instant' );
		} );


	}

	#eventHandlers = {
		/**
		 * Event handler that should be called on scroll or scrollend.
		 */
		handleActiveSlide: (): void => {
			// Return early when #slidingElement is null or undefined.
			if ( ! this.#slidingElement ) return;
			// Set centered element
			let centeredElement: HTMLLIElement;
			const slideTrackClientRect = this.#slidingElement.getBoundingClientRect();
			const alignments = {
				start: slideTrackClientRect.left + 1,
				middle: slideTrackClientRect.left + slideTrackClientRect.width / 2,
				end: slideTrackClientRect.left + slideTrackClientRect.width - 1,
			};
			const verticalLine = alignments[this.#options.alignment];
			this.#totalSliderElements.forEach( element => {
				const elementClientRect = element.getBoundingClientRect();
				if ( verticalLine >= elementClientRect.left
						&& verticalLine <= elementClientRect.right
				) {
					centeredElement = element;
				}
			} );

			if ( centeredElement
					&& centeredElement !== this.#activeSlideElement
			) {


				centeredElement.removeAttribute( 'aria-expanded' );
				this.#totalSliderElements.forEach( element => {
					element.removeAttribute( 'aria-expanded' );
				} );
				this.#activeSlideElement = centeredElement;
				this.#activeSlideIndex = parseInt( centeredElement.getAttribute( 'data-slideindex' ) );
				centeredElement.setAttribute( 'aria-expanded', 'true' );

				if ( ! this.#dotNavigationWrapper ) return;
				Array.from( this.#dotNavigationWrapper.children ).forEach( ( navigationDot ) => {

					if ( ! navigationDot.firstElementChild.hasAttribute( 'data-target-index' ) ) return;
					const buttonElement = navigationDot.firstElementChild;
					if ( buttonElement.getAttribute( 'data-target-index' )
					     === centeredElement.getAttribute( 'data-slideindex' )
					) {
						buttonElement.firstElementChild.classList.remove( 'block' );
						buttonElement.firstElementChild.classList.add( 'hidden' );
						buttonElement.lastElementChild.classList.remove( 'hidden' );
						buttonElement.lastElementChild.classList.add( 'block' );
					} else {
						buttonElement.lastElementChild.classList.remove( 'block' );
						buttonElement.lastElementChild.classList.add( 'hidden' );
						buttonElement.firstElementChild.classList.remove( 'hidden' );
						buttonElement.firstElementChild.classList.add( 'block' );
					}
				} );
			}
		},
		resetScrollPosition: (): void => {
			// Return early when #slidingElement is null or undefined.
			if ( ! this.#slidingElement ) return;

			if (
				this.#activeSlideElement?.getAttribute( 'data-cloned' )
				=== 'start'
			) {
				this.#slidingElement.scrollLeft += (
					this.calculateSlideWidth() *
					this.#originalSliderElements.length
				);
			}
			else if (
				this.#activeSlideElement?.getAttribute( 'data-cloned' )
				=== 'end'
			) {
				this.#slidingElement.scrollLeft -= (
					this.calculateSlideWidth() *
					this.#originalSliderElements.length
				);
			}
			this.#isAdjustingScroll = false;
			this.#resetAutoScroll();
		},
	};

	#duplicateSlides(): void {
		const totalNeededSlides = this.#options.maxVisibleSlides * 0.5;
		const totalCurrentSlides = this.#originalSliderElements.length;

		this.#totalSliderElements = this.#sliderWrapper.querySelectorAll(
			'li[data-slideindex]' );

		if ( totalCurrentSlides === 1 ) return;

		const duplicationFactor = Math.ceil(
			totalNeededSlides / ( totalCurrentSlides || 1 ),
		);

		for ( let i = 0; i < duplicationFactor; i++ ) {
			this.#originalSliderElements.forEach( ( slide ) => {
				const clonedStart = slide.cloneNode( true ) as HTMLLIElement;

				const anchorTags = clonedStart.querySelectorAll( 'a' );
				const buttonTags = clonedStart.querySelectorAll( 'button' );
				clonedStart.setAttribute( 'tabindex', '-1' );
				anchorTags.forEach( ( anchor ) => {
					anchor.setAttribute( 'tabindex', '-1' );
				} );
				buttonTags.forEach( ( anchor ) => {
					anchor.setAttribute( 'tabindex', '-1' );
				} );

				const clonedEnd = clonedStart
					.cloneNode( true ) as HTMLLIElement;

				clonedStart.setAttribute( 'data-cloned', 'start' );
				clonedEnd.setAttribute( 'data-cloned', 'end' );

				this.#slidingElement.insertBefore(
					clonedStart,
					this.#originalSliderElements[0],
				);
				this.#slidingElement.appendChild(
					clonedEnd,
				);
			} );
		}

		this.#totalSliderElements = this.#sliderWrapper.querySelectorAll(
			'li[data-slideindex]' );
	}

	#initSubElements(): void {
		this.#slidingElement = this.#sliderWrapper.querySelector(
			'[data-slider-part="slidebox"]' );

		this.#dotNavigationWrapper = this.#sliderWrapper.querySelector(
			'[data-slider-part="dot-navigation"]' );

		this.#originalSliderElements = this.#sliderWrapper.querySelectorAll(
			'li[data-slideindex]' );

		this.#playPauseButton = this.#sliderWrapper.querySelector(
			'[data-slider-part="playpause-button"] button' );

		this.#chevronLeftElement = this.#sliderWrapper.querySelector(
			'button[data-slider-part="chevron-left"]' );

		this.#chevronRightElement = this.#sliderWrapper.querySelector(
			'button[data-slider-part="chevron-right"]' );

		this.#dotNavigationElements = this.#dotNavigationWrapper?.querySelectorAll(
			'li:not([data-slider-part="playpause-button"])' );
	}

	#initEventHandlers(): void {
		this.#playPauseButton?.addEventListener( 'click', () => {
			this.toggleAutoPlay();
		} );

		if ( ! ( window as Window & typeof globalThis & { chrome?: unknown } ).chrome ) {
			// Use scroll event for non-Chromium browsers as it is
			// more responsive.
			this.#slidingElement?.addEventListener(
				'scroll', this.#eventHandlers.handleActiveSlide );
		} else {
			// Chromium-based browsers have issues with re-flowing elements
			// while they are scrolling, so we need to delay the reflow
			// that is happening in expandCenteredSlide to the end
			// of the scroll.
			this.#slidingElement?.addEventListener(
				'scrollend', this.#eventHandlers.handleActiveSlide );
		}

		// Event that handles scroll overflow and re-scrolls to the
		// original slider items
		this.#slidingElement?.addEventListener(
			'scrollend',
			this.#eventHandlers.resetScrollPosition,
		);

		this.#chevronLeftElement?.addEventListener( 'click', () => {
			this.slideLeft();
		} );

		this.#chevronRightElement?.addEventListener( 'click', () => {
			this.slideRight();
		} );
	}

	#initNavigationPills(): void {
		Array.from(
			// Avoid a TypeError that would occur if this.#dotNavigationElements is null or undefined.
			this.#dotNavigationElements ?? [],
		).forEach(
			( element: HTMLLIElement, index: number ) => {
				if( ! element.firstElementChild.hasAttribute( 'data-target-index' ) ) return;
				element.firstElementChild.addEventListener( 'click', () => {
					const indexNumber = parseInt(
						element.firstElementChild.getAttribute( 'data-target-index' ),
					);
					this.goToSlide( indexNumber );
				} );
			},
		);
	}

	toggleAutoPlay(): void {
		const playSpan =
			this.#playPauseButton.firstElementChild.firstElementChild;
		const pauseSpan =
			this.#playPauseButton.firstElementChild.lastElementChild;

		multiClassToggle( playSpan, 'hidden', 'flex' );
		multiClassToggle( pauseSpan, 'hidden', 'flex' );

		this.#isPlaying = !this.#isPlaying;
		if ( this.#isPlaying ) {
			this.#autoPlayInterval = setInterval( () => {
				this.goToSlide( this.#activeSlideIndex + 1, true );
			}, this.#options.autoPlayDelay );
		} else {
			clearInterval( this.#autoPlayInterval );
		}
	}

	#resetAutoScroll(): void {
		clearInterval( this.#autoPlayInterval );
		if ( this.#isPlaying ) {
			this.#autoPlayInterval = setInterval(
				() => {this.goToSlide( this.#activeSlideIndex + 1, true );},
				this.#options.autoPlayDelay,
			);
		}
	}

	slideRight( isActionByAutoScroll = false, behavior: 'auto'|'instant'|'smooth' = 'smooth' ): void {
		this.goToSlide( this.#activeSlideIndex + 1, isActionByAutoScroll, behavior );
	}

	slideLeft( isActionByAutoScroll = false, behavior: 'auto'|'instant'|'smooth' = 'smooth' ): void {
		this.goToSlide( this.#activeSlideIndex - 1, isActionByAutoScroll, behavior );
	}

	goToSlide( index: number, isActionByAutoScroll = false, behavior: 'auto'|'instant'|'smooth' = 'smooth' ): void {
		if ( ! this.#slidingElement ) return;
		if ( this.#isAdjustingScroll && isActionByAutoScroll ) return;
		if ( this.#activeSlideIndex === index ) return;
		this.#isAdjustingScroll = true;
		index = index % this.#originalSliderElements.length;
		index = index < 0 ? this.#originalSliderElements.length - 1 : index;
		const slideToGoTo = getNearestSlideElement( this.#activeSlideElement, index );
		if ( ! slideToGoTo ) return;
		const slideToGoToIndex = getElementIndex( slideToGoTo );
		const gapSize = window.getComputedStyle( slideToGoTo.parentElement ).gap;
		// This regex removes the units from the gap css value.
		const match = gapSize.match( /(\d+\.?\d*)/ );
		const gapValue = match ? parseFloat( match[1] ) : null;
		this.#slidingElement.scrollTo( {
			top:0,
			left: slideToGoToIndex * ( slideToGoTo.clientWidth + gapValue ) +
				( - this.#slidingElement.clientWidth/2 + slideToGoTo.clientWidth/2 ) *
				Number ( 'middle' === this.#options.alignment ),
			behavior,
		} );
		this.#activeSlideIndex = index;

	}

	calculateSlideWidth(): number {
		const slideStyle = getComputedStyle( this.#originalSliderElements[0] );
		const slideWidth = parseFloat( slideStyle.width );
		const trackStyle = getComputedStyle( this.#slidingElement );
		const trackGap   = parseFloat( trackStyle.gap ) || 0;

		return slideWidth + trackGap;
	}

}

const getNearestSlideElement = (
	slideElement: HTMLLIElement,
	slideIndexValue: number,
): HTMLLIElement|null => {
	if ( ! slideElement ) return null;

	const allElements = Array.from( slideElement.parentElement.children ) as HTMLLIElement[];
	const targetIndex = allElements.indexOf( slideElement );

	let nearestElement: HTMLLIElement = null;
	let minimumDistance = Infinity;

	allElements.forEach( ( element, index ) => {
		if ( parseInt( element.getAttribute( 'data-slideindex' ) ) === slideIndexValue ) {
			const distance = targetIndex - index;
			if ( Math.abs( distance ) <= Math.abs( minimumDistance ) ) {
				minimumDistance = distance;
				nearestElement = element;
			}
		}
	} );
	return nearestElement;
};

const getElementIndex = ( element: HTMLElement ) => {
	if ( ! element || ! element.parentElement ) return -1;
	return Array.from( element.parentElement.children ).indexOf( element );
};
